import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Download, ChevronLeft, Brain, Search, Lightbulb, Globe, Lock } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { formatFraction } from '../lib/utils';
import { getColorForMatiere } from '../lib/matiereColors';
import type { Fiche, Quiz } from '../lib/types';

const FicheDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [fiche, setFiche] = useState<Fiche | null>(null);
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);

  const keyPoints = fiche ? [
    fiche['Key point 1'],
    fiche['Key point 2'],
    fiche['Key point 3'],
    fiche['Key point 4'],
    fiche['Key point 5'],
  ].filter(Boolean) : [];

  const examples = fiche ? [
    fiche['Example 1'],
    fiche['Example 2'],
    fiche['Example 3'],
  ].filter(Boolean) : [];

  useEffect(() => {
    const fetchFicheAndQuizzes = async () => {
      try {
        if (!id) return;

        const { data: ficheData, error: ficheError } = await supabase
          .from('Fiches')
          .select('*')
          .eq('id', id)
          .single();

        if (ficheError) throw ficheError;
        if (!ficheData) throw new Error('Fiche non trouvée');

        setFiche(ficheData);

        const { data: quizData, error: quizError } = await supabase
          .from('quiz')
          .select('*')
          .eq('fiche_id', id)
          .order('created_at', { ascending: false });

        if (quizError) throw quizError;
        setQuizzes(quizData || []);

      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err instanceof Error ? err.message : 'Une erreur est survenue');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFicheAndQuizzes();
  }, [id]);

  const handleToggleVisibility = async () => {
    if (!fiche || !user || fiche.user_id !== user.id) return;

    setIsUpdatingVisibility(true);
    setError(null);

    try {
      const newVisibility = !fiche.public;

      const { error: ficheError } = await supabase
        .from('Fiches')
        .update({ public: newVisibility })
        .eq('id', fiche.id)
        .eq('user_id', user.id);

      if (ficheError) throw ficheError;

      const { data: quizData, error: quizError } = await supabase
        .from('quiz')
        .select('id')
        .eq('fiche_id', fiche.id)
        .maybeSingle();

      if (quizError && !quizError.message.includes('no rows')) {
        throw quizError;
      }

      if (quizData?.id) {
        const { error: quizUpdateError } = await supabase
          .from('quiz')
          .update({ is_public: newVisibility })
          .eq('id', quizData.id)
          .eq('user_id', user.id);

        if (quizUpdateError) throw quizUpdateError;
      }

      setFiche(prev => prev ? { ...prev, public: newVisibility } : null);

      if (quizData?.id) {
        setQuizzes(prev => prev.map(quiz =>
          quiz.id === quizData.id ? { ...quiz, is_public: newVisibility } : quiz
        ));
      }
    } catch (err) {
      console.error('Error toggling visibility:', err);
      setError('Erreur lors de la modification de la visibilité de la fiche');
    } finally {
      setIsUpdatingVisibility(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
      </div>
    );
  }

  if (error || !fiche) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error || 'Fiche non trouvée'}</p>
          <Link
            to="/mes-fiches"
            className="mt-4 inline-flex items-center text-sm text-red-600 hover:text-red-500"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Retour à mes fiches
          </Link>
        </div>
      </div>
    );
  }

  const colors = getColorForMatiere(fiche.matiere || '');
  const titleColor = colors.isDark ? '#ffffff' : '#1a1a1a';
  const subColor = colors.isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.65)';

  const btnOutlineStyle = colors.isDark
    ? { backgroundColor: 'rgba(255,255,255,0.15)', color: '#ffffff', borderColor: 'rgba(255,255,255,0.4)' }
    : { backgroundColor: 'rgba(0,0,0,0.08)', color: titleColor, borderColor: 'rgba(0,0,0,0.2)' };

  const btnOutlineHoverClass = 'transition-colors';

  const btnPrimaryStyle = colors.isDark
    ? { backgroundColor: 'rgba(255,255,255,0.25)', color: '#ffffff', borderColor: 'rgba(255,255,255,0.5)' }
    : { backgroundColor: 'rgba(0,0,0,0.15)', color: titleColor, borderColor: 'rgba(0,0,0,0.3)' };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div className="bg-[#fffdf7] shadow-lg rounded-lg overflow-hidden" style={{ borderTop: `4px solid ${colors.hexBorder}` }}>

        {/* Header */}
        <div className="px-4 sm:px-6 py-3 border-b" style={{ backgroundColor: colors.hex, borderColor: colors.hexBorder }}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold font-handwriting truncate" style={{ color: titleColor }}>
                {fiche?.Title || fiche?.sujet}
              </h1>
              <p className="text-base font-handwriting" style={{ color: subColor }}>
                {fiche?.matiere} - {fiche?.niveau}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {user && fiche.user_id === user.id && (
                <button
                  onClick={handleToggleVisibility}
                  disabled={isUpdatingVisibility}
                  className={`inline-flex items-center px-2 sm:px-3 py-1.5 border rounded-md shadow-sm text-sm font-medium ${btnOutlineHoverClass} ${isUpdatingVisibility ? 'opacity-50 cursor-wait' : ''}`}
                  style={btnOutlineStyle}
                >
                  {fiche.public ? (
                    <>
                      <Globe className="h-4 w-4 sm:mr-2" />
                      <span className="hidden sm:inline">Public</span>
                    </>
                  ) : (
                    <>
                      <Lock className="h-4 w-4 sm:mr-2" />
                      <span className="hidden sm:inline">Privé</span>
                    </>
                  )}
                </button>
              )}
              <button
                className={`inline-flex items-center px-2 sm:px-3 py-1.5 border rounded-md shadow-sm text-sm font-medium ${btnOutlineHoverClass}`}
                style={btnPrimaryStyle}
                onClick={() => window.print()}
              >
                <Download className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Télécharger</span>
              </button>
              <Link
                to="/mes-fiches"
                className={`inline-flex items-center px-2 sm:px-3 py-1.5 border rounded-md shadow-sm text-sm font-medium ${btnOutlineHoverClass}`}
                style={btnOutlineStyle}
              >
                <ChevronLeft className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Mes fiches</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Quiz Section */}
        {quizzes.length > 0 && (
          <div className="px-4 sm:px-6 py-2 border-b" style={{ backgroundColor: colors.hexLight, borderColor: colors.hexBorder + '40' }}>
            <div className="flex items-center justify-between">
              <h2 className="text-base font-medium text-gray-900">Quiz disponible</h2>
              <Link
                to={`/quiz/${quizzes[0].id}`}
                className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white"
                style={{ backgroundColor: colors.hexBorder }}
              >
                <Brain className="h-4 w-4 mr-2" />
                Faire le quiz
              </Link>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="px-4 sm:px-6 py-4">

          {/* Introduction */}
          {fiche?.Introduction && (
            <div className="mb-6">
              <h2 className="text-xl font-bold mb-3 font-handwriting flex items-center" style={{ color: colors.hexBorder }}>
                <Search className="h-5 w-5 mr-2" />
                Introduction
              </h2>
              <div className="bg-white rounded-lg p-4 border" style={{ borderColor: colors.hexBorder + '30', backgroundImage: 'linear-gradient(#eee .1em, transparent .1em)', backgroundSize: '100% 1.5em' }}>
                <p
                  className="text-gray-800 leading-relaxed font-notes text-base"
                  dangerouslySetInnerHTML={{ __html: formatFraction(fiche.Introduction) }}
                />
              </div>
            </div>
          )}

          {/* Points clés */}
          {keyPoints.length > 0 && (
            <div className="mb-6">
              <h2 className="text-xl font-bold mb-3 font-handwriting flex items-center" style={{ color: colors.hexBorder }}>
                <Brain className="h-5 w-5 mr-2" />
                Points clés
              </h2>
              <div className="space-y-3">
                {keyPoints.map((point, index) => (
                  <div key={index} className="flex items-start p-4 rounded-lg border" style={{ backgroundColor: colors.hexLight, borderColor: colors.hexBorder + '40' }}>
                    <div
                      className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full mr-3 font-handwriting font-bold text-sm"
                      style={{ backgroundColor: colors.hex, color: titleColor }}
                    >
                      {index + 1}
                    </div>
                    <p
                      className="text-gray-800 flex-grow font-notes text-base"
                      dangerouslySetInnerHTML={{ __html: formatFraction(point) }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Exemples */}
          {examples.length > 0 && (
            <div className="mb-6">
              <h2 className="text-xl font-bold mb-3 font-handwriting flex items-center" style={{ color: colors.hexBorder }}>
                <Search className="h-5 w-5 mr-2" />
                Exemples
              </h2>
              <div className="space-y-3">
                {examples.map((example, index) => (
                  <div key={index} className="bg-white border border-gray-100 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <Search className="h-4 w-4 mr-2" style={{ color: colors.hexBorder }} />
                      <span className="text-base font-handwriting" style={{ color: colors.hexBorder }}>Exemple {index + 1}</span>
                    </div>
                    <p
                      className="text-gray-800 font-notes text-base"
                      dangerouslySetInnerHTML={{ __html: formatFraction(example) }}
                      style={{ lineHeight: '1.6' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Méthodologie */}
          {fiche?.Rappel && (
            <div className="p-4 rounded-lg border" style={{ backgroundColor: colors.hexLighter, borderColor: colors.hexBorder + '40' }}>
              <div className="flex items-center mb-3">
                <Lightbulb className="h-5 w-5 mr-2" style={{ color: colors.hexBorder }} />
                <h2 className="text-xl font-bold font-handwriting" style={{ color: colors.hexBorder }}>Rappel méthodologique</h2>
              </div>
              <div className="prose max-w-none">
                <p
                  className="text-gray-800 whitespace-pre-line font-notes text-base"
                  dangerouslySetInnerHTML={{ __html: formatFraction(fiche.Rappel) }}
                  style={{ lineHeight: '1.6' }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FicheDetailPage;
